import type { Collections } from '@nuxt/content'
import type {
  Activity,
  Address,
  Appointment,
  Award,
  Education,
  English,
  ExternalProfile,
  PersonInterface,
  PersonName,
  PersonContact,
  Position,
  Publications,
  Review,
} from '~/interfaces/person'
import { About } from '~/models/about'
import Person from '~/models/person'

type PersonRecord = Collections['persons']
type EducationRecord = Array<{
  startDate: string
  endDate?: string
  description: string
}>
type PositionRecord = Array<{
  startDate: string
  endDate?: string
  description: string
}>
type AppointmentRecord = Array<{
  date: string
  description: string
}>

export function formatFullName(person: { name: PersonName }): string {
  const { leadingTitle, first, last, suffixTitle } = person.name

  const base = [leadingTitle, first, last].filter(Boolean).join(' ')
  return suffixTitle ? `${base}, ${suffixTitle}` : base
}

function normalizeName(name: PersonRecord['name']): PersonName {
  return {
    leadingTitle: name.leadingTitle ?? '',
    suffixTitle: name.suffixTitle ?? '',
    first: name.first,
    last: name.last,
  }
}

function normalizeExternalProfiles(
  externalProfiles?: PersonRecord['externalProfiles'],
): Array<ExternalProfile> {
  return (externalProfiles ?? []).map((profile) => ({
    title: profile.title,
    link: profile.link,
  }))
}

function normalizeAddress(address?: PersonRecord['address']): Address {
  return {
    street: address?.street ?? '',
    postcode: address?.postcode ?? '',
    city: address?.city ?? '',
  }
}

function normalizePublications(
  publications?: PersonRecord['publications'],
): Publications {
  return {
    bibtexUrl: publications?.bibtexUrl ?? '',
  }
}

function normalizeEducation(education?: EducationRecord): Array<Education> {
  return (education ?? []).map((item) => ({
    startDate: item.startDate,
    endDate: item.endDate ?? '',
    description: item.description,
  }))
}

function normalizePositions(positions?: PositionRecord): Array<Position> {
  return (positions ?? []).map((item) => ({
    startDate: item.startDate,
    endDate: item.endDate ?? '',
    description: item.description,
  }))
}

function normalizeAppointments(
  appointments?: AppointmentRecord,
): Array<Appointment> {
  return (appointments ?? []).map((item) => ({
    date: item.date,
    description: item.description,
  }))
}

function normalizeAbout(about?: PersonRecord['en']['about']): About {
  return new About(
    about?.researchFoci ?? [],
    normalizeEducation(about?.education),
    normalizePositions(about?.positions),
    normalizeAppointments(about?.appointments),
  )
}

function normalizeActivities(
  activities?: PersonRecord['en']['activities'],
): Activity | undefined {
  if (!activities) {
    return undefined
  }

  const reviewer: Array<Review> = (activities.reviewer ?? []).map((item) => ({
    label: item.label,
    type: item.type,
  }))

  const rawProgramCommittee = activities.programCommittee ?? []

  const programCommittee = rawProgramCommittee.map((item) => ({
    label: item.label,
    description: item.description,
    location: item.location,
    year: String(item.year),
  }))

  return {
    reviewer,
    programCommittee,
  }
}

function normalizeEnglish(en: PersonRecord['en']): English {
  return {
    role: en.role,
    about: normalizeAbout(en.about),
    awards: (en.awards ?? []).map((item): Award => ({
      date: item.date,
      title: item.title,
      issuer: item.issuer,
    })),
    activities: normalizeActivities(en.activities),
  }
}

export function toPersonInterface(person: PersonRecord): PersonInterface {
  const picture = typeof person.picture === 'string' ? person.picture : ''

  return new Person(
    person.path,
    person.personId,
    normalizeName(person.name),
    person.category,
    person.external ?? false,
    person.phone ?? '',
    person.email,
    person.room ?? '',
    picture,
    normalizeExternalProfiles(person.externalProfiles),
    person.website ?? '',
    normalizePublications(person.publications),
    person.projects ?? [],
    normalizeAddress(person.address),
    normalizeEnglish(person.en),
  )
}

export function toPersonContact(person: PersonRecord): PersonContact {
  return {
    personId: person.personId,
    name: normalizeName(person.name),
    email: person.email,
  }
}
